// XSM-1536 - SSO Login flow
// Author: Abhishek Bharti
import { useEffect, useState } from "react";
import DeviceManager from "../assets/img/Device_Manager.png";
import Integrations from "../assets/img/App_Registry.png";
import Partner from "../assets/img/Access_Manager.png";

const ORGANISATION_ENDPOINT = "/xsm-api/v1/organization";
const USER_ENDPOINT = "/xsm-api/v1/user";
const XSM_BASE_URL = process.env.REACT_APP_XSM_BASE_URL as string;
const USER_DOMAIN = process.env.REACT_APP_USER_DOMAIN as string;
const XSM_ORIGIN = process.env.REACT_APP_XSM_ORIGIN as string;
const MASTER_CALLBACK_URL = process.env.REACT_APP_MASTER_CALLBACK_URL as string;
const PARTNER_CALLBACK_URL = process.env
  .REACT_APP_PARTNER_CALLBACK_URL as string;
const DEVICE_MANAGER_CALLBACK_URL = process.env
  .REACT_APP_DEVICE_MANAGER_CALLBACK_URL as string;
const XSM_MASTER_SUB_DOMAIN =
  process.env.REACT_APP_XSM_MASTER_SUB_DOMAIN as string;
const XSM_PARTNER_SUB_DOMAIN =
  process.env.REACT_APP_XSM_PARTNER_SUB_DOMAIN as string;
const XSM_DEVICE_SUB_DOMAIN =
  process.env.REACT_APP_XSM_DEVICE_SUB_DOMAIN as string;

type Organisation = {
  id?: string | number;
  name?: string;
};

type OrganisationApiItem = {
  org_uuid?: string | number;
  org_name?: string;
};

export type ProductListItem = {
  id: string | number;
  name: string;
  icon: string;
  link: string;
};

type UserDetailsApiOrganization = {
  name: string;
  id: string;
  application: unknown[];
};

type UserDetailsApiResponse = {
  statusCode?: number;
  isSuccess?: boolean;
  message?: string;
  totalRecords?: number;
  errorMessages?: unknown[];
  result?: {
    users?: Array<{
      organizations?: UserDetailsApiOrganization[];
      [key: string]: unknown;
    }>;
  };
};

type OrganisationApiResponse = {
  result?: {
    organizations?: OrganisationApiItem[];
    organisation?: OrganisationApiItem[];
  };
  organizations?: OrganisationApiItem[];
  organisation?: OrganisationApiItem[];
};

const SUBDOMAIN_ORG_EXCLUSION_MAP: Record<string, string> = {
  [XSM_MASTER_SUB_DOMAIN]: "XSM-Master",
  [XSM_PARTNER_SUB_DOMAIN]: "Partner",
  [XSM_DEVICE_SUB_DOMAIN]: "DeviceManager",
};

const ORGANISATION_PRODUCT_CONFIG: Record<
  string,
  { displayName: string; icon: string; link: () => string }
> = {
  "XSM-Master": {
    displayName: "App Registry",
    icon: Integrations,
    link: () => MASTER_CALLBACK_URL,
  },
  Partner: {
    displayName: "Access Manager",
    icon: Partner,
    link: () => PARTNER_CALLBACK_URL,
  },
  DeviceManager: {
    displayName: "Device Manager",
    icon: DeviceManager,
    link: () => DEVICE_MANAGER_CALLBACK_URL,
  },
};

const getSubdomain = () => {
  if (typeof window === "undefined") {
    return "";
  }

  const hostname = window.location.hostname;
  if (!hostname || hostname === "localhost") {
    return (localStorage.getItem("domain") || "").toLowerCase();
  }

  const [subdomain] = hostname.split(".");
  return (subdomain || "").toLowerCase();
};

export const productListMapping = (
  organisations: Organisation[],
): ProductListItem[] => {
  if (!(organisations instanceof Array)) {
    return [];
  }

  const subdomain = getSubdomain() || "";
  const excludedOrganisation = SUBDOMAIN_ORG_EXCLUSION_MAP[subdomain];
  // Always exclude SYSTEM_ORG and EvesSysAdmin
  const excludedOrganisations = ["SYSTEM_ORG", "EvesSysAdmin"];

  return organisations
    .filter(
      (organisation) =>
        organisation?.name &&
        !excludedOrganisations.includes(organisation.name),
    )
    .filter(
      (organisation) =>
        !excludedOrganisation || organisation?.name !== excludedOrganisation,
    )
    .map<ProductListItem | null>((organisation, index) => {
      const config = organisation?.name
        ? ORGANISATION_PRODUCT_CONFIG[organisation.name]
        : undefined;
      if (!config) {
        return null;
      }

      return {
        id: organisation?.id ?? index,
        name: config.displayName,
        icon: config.icon,
        link: config.link(),
      };
    })
    .filter((item): item is ProductListItem => Boolean(item));
};

export const fetchUserDetails = async (): Promise<Organisation[]> => {
  const token = localStorage.getItem("access_token");
  const userName = localStorage.getItem("username");
  if (!token || !userName) {
    return [];
  }

  const url = new URL(`${XSM_BASE_URL}${USER_ENDPOINT}`);
  url.searchParams.set("userName", userName);
  url.searchParams.set("domainName", USER_DOMAIN);

  const userResponse = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "XSM-Domain": USER_DOMAIN,
      "XSM-Internal": "true",
      "X-Original-Origin": XSM_ORIGIN,
    },
  });

  if (!userResponse.ok) {
    const errorBody = await userResponse.text().catch(() => "");
    throw new Error(
      `Failed to fetch user details: ${userResponse.status} ${errorBody}`,
    );
  }

  const response: UserDetailsApiResponse = await userResponse.json();
  const userData = response?.result?.users?.[0];
  const organizationData = userData?.organizations || [];

  if (!Array.isArray(organizationData)) {
    return [];
  }

  // Map organizations from user details API to Organisation format
  // The API already provides 'name' and 'id' fields, so we can use them directly
  return organizationData.map<Organisation>((org) => ({
    id: org?.id,
    name: org?.name,
  }));
};

export const fetchOrganizations = async (): Promise<Organisation[]> => {
  const token = localStorage.getItem("access_token");

  const domainName = localStorage.getItem("domain") || "";
  const url = new URL(`${XSM_BASE_URL}${ORGANISATION_ENDPOINT}`);
  url.searchParams.set("domainName", domainName);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      ...(domainName ? { "XSM-Domain": domainName } : {}),
      "xsm-internal": "true",
      "X-Original-Origin": "dev.eves360.ai",
    },
  });

  const data: OrganisationApiResponse = await response.json();
  const organisations = data?.result?.organizations ?? [];

  if (!Array.isArray(organisations)) {
    return [];
  }

  return organisations.map<Organisation>((organisation, index) => ({
    id: organisation?.org_uuid ?? index,
    name: organisation?.org_name,
  }));
};

export const useProductList = () => {
  const [products, setProducts] = useState<ProductListItem[]>([]);

  useEffect(() => {
    let isMounted = true;

    const loadOrganisations = async () => {
      try {
        const organisations = await fetchUserDetails();
        const mappedProducts = productListMapping(organisations);
        if (isMounted) {
          setProducts(mappedProducts);
        }
      } catch (error) {
        if (isMounted) {
          setProducts([]);
        }
      }
    };

    loadOrganisations();

    return () => {
      isMounted = false;
    };
  }, []);

  return { products };
};
